// Convertir lead (Parte 6, T-601): contacto + cuenta (crear/asociar) + negocio opcional.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.ts";
import { usePipelines } from "../lib/lookups.ts";
import type { Row } from "../lib/resource.ts";
import { Button, Card, Field, Input, Loading, Select } from "../components/ui.tsx";
import { toast } from "../components/toast.tsx";

interface Suggestion { id: string; accountName: string; score?: number; }

export function ConvertLead(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: pipelines } = usePipelines();

  const lead = useQuery<{ lead: Row }>({ queryKey: ["record", "leads", id], enabled: Boolean(id), queryFn: () => api.get(`/leads/${id}`) });
  const sugg = useQuery<{ leadCompany: string; suggestions: Suggestion[] }>({
    queryKey: ["convert-suggestions", id], enabled: Boolean(id), queryFn: () => api.get(`/leads/${id}/convert/suggestions`),
  });

  const [accountMode, setAccountMode] = useState<"create" | "associate">("create");
  const [associateId, setAssociateId] = useState("");
  const [createDeal, setCreateDeal] = useState(false);
  const [dealName, setDealName] = useState("");
  const [amount, setAmount] = useState("");

  const defaultPipeline = pipelines?.pipelines.find((p) => p.isDefault) ?? pipelines?.pipelines[0];
  const firstOpenStage = defaultPipeline?.stages.find((s) => s.stageType === "open");

  const convert = useMutation({
    mutationFn: (body: Row) => api.post<{ contactId: string; accountId: string; dealId: string | null }>(`/leads/${id}/convert`, body),
    onSuccess: (r) => {
      toast.success("Lead convertido.");
      if (r.dealId) navigate(`/deals/${r.dealId}`);
      else navigate(`/contacts/${r.contactId}`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo convertir."),
  });

  if (lead.isLoading || !lead.data) return <Loading />;
  const l = lead.data.lead;
  const company = String(l["company"] ?? "");

  const submit = (): void => {
    const account: Row = accountMode === "create"
      ? { action: "create", name: company }
      : { action: "associate", accountId: associateId };
    if (accountMode === "associate" && !associateId) { toast.error("Selecciona una cuenta existente."); return; }
    const body: Row = { account };
    if (createDeal) {
      const deal: Row = { dealName: dealName || `${company} - Negocio` };
      if (amount) deal["amount"] = Number(amount);
      if (defaultPipeline) deal["pipelineId"] = defaultPipeline.id;
      if (firstOpenStage) deal["stageId"] = firstOpenStage.id;
      deal["closingDate"] = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
      body["deal"] = deal;
    }
    convert.mutate(body);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <button className="btn-ghost btn-sm" onClick={() => navigate(`/leads/${id}`)}>←</button>
        <h1 className="text-xl font-bold text-ink">Convertir lead</h1>
      </div>

      <Card className="p-5 space-y-3">
        <h3 className="text-sm font-semibold text-ink-secondary">Contacto</h3>
        <p className="text-sm text-ink-secondary">
          Se creará el contacto <b>{`${String(l["firstName"] ?? "")} ${String(l["lastName"] ?? "")}`.trim()}</b> con los datos del lead.
        </p>
      </Card>

      <Card className="p-5 space-y-3">
        <h3 className="text-sm font-semibold text-ink-secondary">Cuenta</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" checked={accountMode === "create"} onChange={() => setAccountMode("create")} />
          Crear cuenta «{company}»
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" checked={accountMode === "associate"} onChange={() => setAccountMode("associate")} />
          Asociar a una cuenta existente
        </label>
        {accountMode === "associate" && (
          <Field label="Cuenta">
            <Select value={associateId} onChange={(e) => setAssociateId(e.target.value)}>
              <option value="">—</option>
              {(sugg.data?.suggestions ?? []).map((s) => <option key={s.id} value={s.id}>{s.accountName}</option>)}
            </Select>
          </Field>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-ink-secondary">
          <input type="checkbox" checked={createDeal} onChange={(e) => setCreateDeal(e.target.checked)} /> Crear negocio
        </label>
        {createDeal && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre del negocio"><Input value={dealName} placeholder={`${company} - Negocio`} onChange={(e) => setDealName(e.target.value)} /></Field>
            <Field label="Monto"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
          </div>
        )}
      </Card>

      <div className="flex justify-end gap-2">
        <Button onClick={() => navigate(`/leads/${id}`)}>Cancelar</Button>
        <Button variant="primary" onClick={submit} disabled={convert.isPending}>{convert.isPending ? "Convirtiendo…" : "Convertir"}</Button>
      </div>
    </div>
  );
}
